/**
 * Fusion Priority Config Component
 * 
 * Admin interface for managing broker priority configuration for the Tick Fusion Engine.
 * Features:
 * - Exchange-specific priority configuration (NSE, NFO, MCX, etc.)
 * - View/edit priority for price, metadata, and depth data types
 * - Reorder broker priority: drag-and-drop on desktop, up/down arrows on touch
 *   (HTML5 drag events never fire from touch, so the arrows are the only
 *   reorder path on phone/tablet — not a convenience)
 * - Add/remove brokers from each priority type
 * - Changes push to Redis and notify aggregator via pub/sub
 * 
 * @module MarketDataManagement/components/FusionPriorityConfig
 */

import React, { useState, useEffect, useCallback } from 'react';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import useBreakpoint from '../../../../hooks/useBreakpoint';
import {
  FiPlus,
  FiTrash2,
  FiRefreshCw,
  FiMove,
  FiX,
  FiZap,
  FiDatabase,
  FiBarChart2,
  FiGlobe,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';

// Priority type icons and descriptions
const PRIORITY_TYPES = {
  price: { 
    label: 'Price (LTP)', 
    icon: FiZap, 
    color: 'green',
    description: 'Fast price updates'
  },
  metadata: { 
    label: 'Metadata (OI)', 
    icon: FiDatabase, 
    color: 'blue',
    description: 'OI, Circuit Limits'
  },
  depth: { 
    label: 'Depth (L5)', 
    icon: FiBarChart2, 
    color: 'purple',
    description: 'L5 bid/ask'
  }
};

// Supported exchanges (matches backend SUPPORTED_EXCHANGES)
const EXCHANGES = ['ALL', 'NSE', 'NFO', 'MCX', 'CDS', 'BSE', 'BFO'];

const FusionPriorityConfig = () => {
  const { showSnackbar } = useSnackbar();
  // phone + tablet: HTML5 drag never fires from touch, so these tiers get arrows.
  const { isCompact } = useBreakpoint();

  // State
  const [loading, setLoading] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState('ALL');
  // Nested structure: { exchange: { type: [brokers] } }
  const [allPriorities, setAllPriorities] = useState({});
  const [availableBrokers, setAvailableBrokers] = useState([]);
  const [dragState, setDragState] = useState({ isDragging: false, source: null });
  const [addBrokerPopover, setAddBrokerPopover] = useState({ open: false, type: null });
  const [newBrokerName, setNewBrokerName] = useState('');
  const [expandedSections, setExpandedSections] = useState({ price: true, metadata: true, depth: true });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch current priority config from MDMS (nested by exchange)
      const response = await services.mdms.getBrokerPriority();
      if (response.data && response.data.exchanges) {
        setAllPriorities(response.data.exchanges);
      }
      
      // Fetch available brokers from BIS
      try {
        const brokersResponse = await services.brokerPlatforms.getAvailableBrokers();
        if (brokersResponse.data) {
          const brokerList = Array.isArray(brokersResponse.data) 
            ? brokersResponse.data 
            : brokersResponse.data.brokers || brokersResponse.data.data || [];
            
          // Filter active brokers and map to standardized format
          const activeBrokers = brokerList
            .filter(b => b.is_active !== false)
            .map(b => ({
              id: b.internal_name || b.broker_name || b.name, // Use internal_name/broker_name as simplified ID
              name: b.internal_name || b.broker_name || b.name,
              displayName: b.display_name || b.broker_name || b.name || b.internal_name,
              ...b
            }));
            
          setAvailableBrokers(activeBrokers);
        }
      } catch (err) {
        console.log('Could not fetch broker list, using defaults');
        setAvailableBrokers([
          { id: 'flattrade', name: 'flattrade', displayName: 'Flattrade' }, 
          { id: 'icici', name: 'icici', displayName: 'ICICI Direct' }, 
          { id: 'zerodha', name: 'zerodha', displayName: 'Zerodha' },
          { id: 'upstox', name: 'upstox', displayName: 'Upstox' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching priority config:', error);
      showSnackbar(error.message || 'Failed to load priority configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Get current priorities for selected exchange (with fallback to empty)
  const getCurrentPriorities = () => {
    return allPriorities[selectedExchange] || { price: [], metadata: [], depth: [] };
  };

  const savePriority = async (priorityType, brokers) => {
    try {
      // API path now includes exchange: PUT /config/broker-priority/{exchange}/{type}
      await services.mdms.updateBrokerPriority(`${selectedExchange}/${priorityType}`, { brokers });
      showSnackbar(`Updated ${selectedExchange} ${PRIORITY_TYPES[priorityType].label}`, 'success');
    } catch (error) {
      console.error('Error saving priority:', error);
      showSnackbar(error.message || 'Failed to save priority', 'error');
    }
  };

  // The one reorder path. Both drag-and-drop (desktop) and the up/down arrows
  // (touch) funnel through here so the two can never drift apart.
  const reorderBroker = async (type, srcIndex, targetIndex) => {
    const currentPriorities = getCurrentPriorities();
    const list = currentPriorities[type] || [];
    if (srcIndex === targetIndex) return;
    if (targetIndex < 0 || targetIndex >= list.length) return;

    const newBrokers = [...list];
    const [moved] = newBrokers.splice(srcIndex, 1);
    newBrokers.splice(targetIndex, 0, moved);

    // Optimistic update
    setAllPriorities(prev => ({
      ...prev,
      [selectedExchange]: {
        ...(prev[selectedExchange] || {}),
        [type]: newBrokers
      }
    }));

    await savePriority(type, newBrokers);
  };

  // Drag handlers (desktop only — touch never fires these)
  const handleDragStart = (e, type, index) => {
    setDragState({ isDragging: true, source: { type, index } });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, type, targetIndex) => {
    e.preventDefault();
    if (!dragState.source || dragState.source.type !== type) {
      setDragState({ isDragging: false, source: null });
      return;
    }

    const { index: srcIndex } = dragState.source;
    setDragState({ isDragging: false, source: null });
    await reorderBroker(type, srcIndex, targetIndex);
  };

  const handleDragEnd = () => {
    setDragState({ isDragging: false, source: null });
  };

  const addBroker = async (type, brokerName) => {
    try {
      console.log('[FusionPriority] addBroker called:', { type, brokerName, selectedExchange });
      if (!brokerName) {
        console.log('[FusionPriority] No broker name provided');
        return;
      }

      const normalizedName = brokerName.toLowerCase().trim();
      if (!normalizedName) {
        console.log('[FusionPriority] Normalized name is empty');
        return;
      }
      
      const currentPriorities = getCurrentPriorities();
      const currentList = currentPriorities[type] || [];
      
      console.log('[FusionPriority] Current list:', currentList);
      
      if (currentList.includes(normalizedName)) {
        console.log('[FusionPriority] Broker already in list');
        showSnackbar('Broker already in list', 'warning');
        return;
      }

      const newBrokers = [...currentList, normalizedName];
      console.log('[FusionPriority] New broker list:', newBrokers);
      
      // Optimistic update
      setAllPriorities(prev => {
        const newState = {
          ...prev,
          [selectedExchange]: {
            ...(prev[selectedExchange] || {}),
            [type]: newBrokers
          }
        };
        console.log('[FusionPriority] Setting new state:', newState);
        return newState;
      });

      setAddBrokerPopover({ open: false, type: null });
      setNewBrokerName('');
      
      console.log('[FusionPriority] Calling API...');
      await services.mdms.addBrokerToType(selectedExchange, type, normalizedName);
      console.log('[FusionPriority] API call successful');
      showSnackbar(`Added ${normalizedName}`, 'success');
    } catch (error) {
      console.error('[FusionPriority] ERROR in addBroker:', error);
      showSnackbar(`Failed to add broker: ${error.message || 'Unknown error'}`, 'error');
      fetchData(); // Refresh on error
    }
  };


  const removeBroker = async (type, brokerIndex) => {
    const currentPriorities = getCurrentPriorities();
    const brokerToRemove = currentPriorities[type][brokerIndex];
    if (!brokerToRemove) return;

    const newBrokers = currentPriorities[type].filter((_, i) => i !== brokerIndex);
    
    // Update local state
    setAllPriorities(prev => ({
      ...prev,
      [selectedExchange]: {
        ...(prev[selectedExchange] || {}),
        [type]: newBrokers
      }
    }));
    
    try {
      await services.mdms.removeBrokerFromType(selectedExchange, type, brokerToRemove);
      showSnackbar(`Removed ${brokerToRemove}`, 'success');
    } catch (error) {
      console.error('Error removing broker:', error);
      showSnackbar('Failed to remove broker', 'error');
      fetchData(); // Refresh on error
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-profit text-white';
    if (rank === 2) return 'bg-accent text-white';
    if (rank === 3) return 'bg-warning text-white';
    return 'bg-bg-3 text-fg-2';
  };

  const toggleSection = (type) => {
    setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <FiRefreshCw className="animate-spin text-2xl text-accent mr-2" />
        <span className="text-fg-2">Loading...</span>
      </div>
    );
  }

  const currentPriorities = getCurrentPriorities();
  const totalRules = Object.values(currentPriorities).reduce((acc, list) => acc + (list?.length || 0), 0);

  return (
    <div className="space-y-3">
      {/* Header with Exchange Selector — stacks below sm: the 7 exchange tabs and
          the title cannot share one row at phone width. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-bg-2 rounded-lg border border-[var(--divider)]">
        <div className="flex items-center gap-3">
          <FiGlobe className="text-accent shrink-0" />
          <span className="text-sm font-medium text-fg-2">Fusion Engine Priority</span>
          <span className="text-xs text-fg-3">({totalRules} rules)</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          {/* Exchange Tabs — scroll rather than squash; 7 tabs never fit 343px. */}
          <div className="flex-1 min-w-0 overflow-x-auto">
            <div className="flex gap-1 bg-bg-3 rounded-md p-0.5 w-max">
              {EXCHANGES.map(ex => (
                <button
                  key={ex}
                  onClick={() => setSelectedExchange(ex)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                    selectedExchange === ex
                      ? 'bg-bg-2 text-accent shadow-sm'
                      : 'text-fg-2 hover:text-fg-1'
                  }`}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={fetchData}
            className="p-2 shrink-0 text-fg-2 hover:text-fg-1 hover:bg-bg-3 rounded-md transition-colors"
            title="Refresh"
          >
            <FiRefreshCw className="text-sm" />
          </button>
        </div>
      </div>

      {/* Priority Sections */}
      <div className="space-y-2">
        {Object.entries(PRIORITY_TYPES).map(([type, config]) => {
          const Icon = config.icon;
          const brokers = currentPriorities[type] || [];
          const isPopoverOpen = addBrokerPopover.open && addBrokerPopover.type === type;
          const isExpanded = expandedSections[type];

          return (
            <div key={type} className="bg-bg-2 rounded-lg border border-[var(--divider)] overflow-visible">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(type)}
                className={`w-full flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-600 to-slate-700 dark:from-slate-700 dark:to-slate-800 text-white hover:from-slate-700 hover:to-slate-800 transition-all rounded-t-lg ${!isExpanded ? 'rounded-b-lg' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs">{isExpanded ? '▼' : '▶'}</span>
                  <Icon className="text-sm" />
                  <span className="text-sm font-semibold">{config.label}</span>
                  <span className="text-xs opacity-60 ml-1">{config.description}</span>
                </div>
                <span className="text-xs opacity-60">{brokers.length} brokers</span>
              </button>

              {/* Collapsible Content */}
              {isExpanded && (
                <div className="p-2 overflow-visible">
                  <div className="border border-[var(--divider)] rounded-md overflow-visible">
                    <div className="flex items-center justify-between px-2.5 py-1.5 bg-bg-3/50">
                      <span className="text-xs font-medium text-fg-2">Active Brokers</span>
                    </div>

                    <div className="p-2 overflow-visible">
                      {brokers.length === 0 && (
                        <div className="text-center py-3 text-fg-3 text-[10px]">
                          No brokers configured. Click + to add.
                        </div>
                      )}
                      
                      <div className={isCompact ? 'space-y-1.5' : 'flex flex-wrap gap-1.5 items-center'}>
                        {brokers.map((broker, index) => isCompact ? (
                          /* Touch tier: a real list with arrows. Drag is impossible
                             here, so these buttons are the ONLY way to reorder. */
                          <div
                            key={`${type}_${broker}`}
                            className="flex items-center gap-2 rounded-md border border-[var(--divider)] bg-bg-2 pl-2 pr-1 py-1"
                          >
                            <span className={`text-[10px] font-bold w-5 h-5 shrink-0 flex items-center justify-center rounded ${getRankBadge(index + 1)}`}>
                              {index + 1}
                            </span>
                            <span className="flex-1 min-w-0 truncate text-sm font-medium text-fg-2 capitalize">
                              {broker}
                            </span>
                            <button
                              onClick={() => reorderBroker(type, index, index - 1)}
                              disabled={index === 0}
                              className="h-11 w-11 shrink-0 flex items-center justify-center rounded-md text-fg-2 hover:bg-bg-3 disabled:opacity-25 transition-colors"
                              aria-label={`Move ${broker} up`}
                            >
                              <FiArrowUp className="text-sm" />
                            </button>
                            <button
                              onClick={() => reorderBroker(type, index, index + 1)}
                              disabled={index === brokers.length - 1}
                              className="h-11 w-11 shrink-0 flex items-center justify-center rounded-md text-fg-2 hover:bg-bg-3 disabled:opacity-25 transition-colors"
                              aria-label={`Move ${broker} down`}
                            >
                              <FiArrowDown className="text-sm" />
                            </button>
                            <button
                              onClick={() => removeBroker(type, index)}
                              className="h-11 w-11 shrink-0 flex items-center justify-center rounded-md text-fg-3 hover:text-loss hover:bg-bg-3 transition-colors"
                              aria-label={`Remove ${broker}`}
                            >
                              <FiTrash2 className="text-sm" />
                            </button>
                          </div>
                        ) : (
                          <div
                            key={`${type}_${broker}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, type, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, type, index)}
                            onDragEnd={handleDragEnd}
                            className={`
                              group flex items-center gap-1.5 pl-1 pr-1.5 py-1 rounded-md border transition-all cursor-move
                              bg-bg-2 border-[var(--divider)] hover:border-accent
                              ${dragState.isDragging && dragState.source?.type === type && dragState.source?.index === index
                                ? 'opacity-40 scale-95' : ''}
                            `}
                          >
                            <FiMove className="text-[10px] text-fg-3 cursor-grab" />
                            <span className={`text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded ${getRankBadge(index + 1)}`}>
                              {index + 1}
                            </span>
                            <span className="text-xs font-medium text-fg-2 capitalize">
                              {broker}
                            </span>
                            <button
                              onClick={() => removeBroker(type, index)}
                              className="p-0.5 text-fg-3 hover:text-loss hover:bg-bg-3 rounded transition-colors opacity-0 group-hover:opacity-100"
                              title="Remove"
                            >
                              <FiTrash2 className="text-[10px]" />
                            </button>
                          </div>
                        ))}

                        {/* Add Broker Button */}
                        <div className="relative">
                          <button
                            onClick={() => setAddBrokerPopover(
                              isPopoverOpen ? { open: false, type: null } : { open: true, type }
                            )}
                            className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-medium rounded-md border transition-colors text-accent bg-accent/10 hover:bg-accent/20 border-accent/30"
                          >
                            <FiPlus className="text-xs" />
                            Add
                          </button>

                          {/* Add Broker Dropdown - Shows ALL available brokers */}
                          {isPopoverOpen && (
                            <div className="absolute left-0 top-full mt-1 w-52 bg-bg-2 rounded-md shadow-lg border border-[var(--divider)] z-20">
                              <div className="p-2 border-b border-[var(--divider)] flex items-center justify-between">
                                <span className="text-xs font-medium text-fg-2">Select Broker</span>
                                <button
                                  onClick={() => setAddBrokerPopover({ open: false, type: null })}
                                  className="p-2 -m-1 text-fg-3 hover:text-fg-1 rounded transition-colors"
                                  aria-label="Close"
                                >
                                  <FiX className="text-xs" />
                                </button>
                              </div>
                              
                              <div className="max-h-48 overflow-y-auto py-1">
                                {availableBrokers.map(broker => {
                                  // broker object from our state mapping has: { id, name, displayName, ... }
                                  // We mapped it in fetchData: id = internal_name, name = internal_name, displayName = display_name
                                  const brokerIdentifier = broker.id || broker.name || '';
                                  const brokerDisplayName = broker.displayName || broker.name || broker.id || 'Unknown';
                                  const isAlreadyAdded = brokers.includes((broker.name || broker.id || '').toLowerCase());
                                  
                                  return (
                                    <button
                                      key={broker.id || broker.name || Math.random()} 
                                      onClick={() => {
                                        console.log('[FusionPriority] Broker clicked:', broker);
                                        addBroker(type, broker.name || broker.id);
                                      }}
                                      disabled={isAlreadyAdded}
                                      className={`w-full text-left px-3 py-2.5 text-xs capitalize transition-colors flex items-center justify-between ${
                                        isAlreadyAdded
                                          ? 'text-fg-3 cursor-not-allowed'
                                          : 'text-fg-2 hover:bg-bg-3'
                                      }`}
                                    >
                                      <span>{brokerDisplayName}</span>
                                      {isAlreadyAdded && <span className="text-[10px] italic">(added)</span>}
                                    </button>
                                  );
                                })}
                              </div>

                              <div className="p-2 border-t border-[var(--divider)]">
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={newBrokerName}
                                    onChange={(e) => setNewBrokerName(e.target.value)}
                                    placeholder="Custom..."
                                    className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-[var(--divider)] rounded bg-bg-3 text-fg-1"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => addBroker(type, newBrokerName)}
                                    disabled={!newBrokerName.trim()}
                                    className="btn btn-primary btn-sm disabled:opacity-50"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help */}
      <div className="p-2.5 bg-bg-3 dark:bg-bg-1/50 rounded-lg text-[10px] text-fg-3">
        <span className="font-medium">Tips:</span> Select exchange → Configure priority per data type →{' '}
        {isCompact ? 'use the arrows to reorder' : 'drag to reorder'}
      </div>
    </div>
  );
};

export default FusionPriorityConfig;
